/**
 * Sentry para el frontend.
 *
 * Init lazy — si VITE_SENTRY_DSN no está, no se inicializa y todas las llamadas son no-op.
 * Captura errores no controlados (window.onerror), errores en React (vía boundary),
 * y trazas de performance.
 */

import * as Sentry from "@sentry/react";
import { isChunkLoadError } from "./chunk-error.js";

let initialized = false;

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialized) return;

  try {
    Sentry.init({
      dsn,
      environment:        import.meta.env.MODE,            // "development" | "production"
      release:            import.meta.env.VITE_APP_VERSION || "unknown",
      tracesSampleRate:   parseFloat(import.meta.env.VITE_SENTRY_TRACES_RATE || "0.2"),
      // Replay desactivado por privacidad (graba interacciones del usuario)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,

      // Filtros para no llenar la cuota con ruido
      beforeSend(event) {
        const msg = event.message || event.exception?.values?.[0]?.value || "";
        // Errores conocidos de extensiones de browser
        if (msg.includes("ResizeObserver loop"))            return null;
        if (msg.includes("Non-Error promise rejection"))    return null;
        if (msg.match(/network.*error/i))                   return null;  // offline transitorio
        // 401 Unauthorized son esperables al expirar el token
        if (msg.includes("Unauthorized"))                   return null;
        // Chunk viejo tras un deploy — main.jsx ya lo recarga solo (1 vez por
        // sesión), no hace falta que dispare una alerta de alta prioridad.
        if (isChunkLoadError(msg))                          return null;
        return event;
      },
    });
    initialized = true;
    if (import.meta.env.DEV) {
      console.log("[Sentry] Inicializado", { env: import.meta.env.MODE });
    }
  } catch (err) {
    console.warn("[Sentry] Init falló:", err.message);
  }
}

/**
 * Etiqueta al usuario logueado para correlacionar errores con personas reales.
 * Llamar después del login (AuthContext).
 */
export function setSentryUser(user) {
  if (!initialized || !user) return;
  Sentry.setUser({
    id:       user.uid,
    email:    user.email,
    username: user.displayName || user.email,
  });
}

export function clearSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * ErrorBoundary para envolver el App o secciones de la UI.
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * Captura manual de errores con contexto.
 */
export function captureException(err, context = {}) {
  if (!initialized) {
    console.error("[capture]", err, context);
    return;
  }
  Sentry.withScope(scope => {
    Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
    Sentry.captureException(err);
  });
}

export function isInitialized() {
  return initialized;
}
